import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plane, Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDestinationsOpen, setIsDestinationsOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.routes'), path: '/rutas' },
    { 
      name: t('nav.destinations'), 
      path: '#',
      submenu: [
        { name: t('nav.puertoEscondido'), path: '/destinos/puerto-escondido' },
        { name: t('nav.huatulco'), path: '/destinos/huatulco' },
        { name: t('nav.oaxaca'), path: '/destinos/oaxaca' }
      ]
    },
    { name: t('nav.privateFlights'), path: '/vuelos-privados' },
    { name: t('nav.about'), path: '/sobre-nosotros' },
    { name: t('nav.information'), path: '/informacion' },
    { name: t('nav.contact'), path: '/contacto' }
  ];

  const isActive = (path) => {
    if (path === '#') return false;
    return location.pathname === path;
  };

  const isDestinationActive = () => {
    return location.pathname.startsWith('/destinos/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/images/logo.png" 
              alt="Aerotucán Logo" 
              className="h-16 w-auto transform transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              item.submenu ? (
                <div 
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setIsDestinationsOpen(true)}
                  onMouseLeave={() => setIsDestinationsOpen(false)}
                >
                  <button
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center ${
                      isDestinationActive()
                        ? 'bg-blue-900 text-white'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-900'
                    }`}
                  >
                    {item.name}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isDestinationsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isDestinationsOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 animate-in slide-in-from-top-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isActive(subItem.path)
                              ? 'bg-blue-50 text-blue-900 font-semibold'
                              : 'text-gray-700 hover:bg-blue-50 hover:text-blue-900'
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? 'bg-blue-900 text-white'
                      : 'text-gray-700 hover:bg-blue-50 hover:text-blue-900'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:+529515020840" className="flex items-center space-x-2 text-gray-700 hover:text-blue-900 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">951 502 0840</span>
            </a>
            <Link to="/rutas">
              <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg">
                {t('nav.searchFlights')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top">
            <nav className="flex flex-col space-y-2">
              {navItems.map((item) => (
                item.submenu ? (
                  <div key={item.name} className="px-4">
                    <button
                      onClick={() => setIsDestinationsOpen(!isDestinationsOpen)}
                      className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700"
                    >
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isDestinationsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isDestinationsOpen && (
                      <div className="pl-4 space-y-1 mt-2">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block px-3 py-2 rounded-lg text-sm ${
                              isActive(subItem.path)
                                ? 'bg-blue-900 text-white font-semibold'
                                : 'text-gray-600 hover:bg-blue-50'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-blue-900 text-white'
                        : 'text-gray-700 hover:bg-blue-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <a
                href="tel:+529515020840"
                className="px-4 py-3 rounded-lg text-sm font-medium text-blue-900 hover:bg-blue-50 flex items-center space-x-2"
              >
                <Phone className="w-4 h-4" />
                <span>951 502 0840</span>
              </a>
              <Link to="/rutas" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                  {t('nav.searchFlights')}
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
